const _ = require('lodash')
const nameUtils = require('shared/name-utils')
const toast = require('client/lib/toast')
const viewLocals = require('client/lib/view-locals')
const { alert } = require('client/components/confirm')
const ArticleModel = require('client/models/article')
const MixModel = require('client/models/mix')
const UserModel = require('client/models/user')
const mixListItemsTemplate = require('*views/partials/mix-list-items')
const Promise = require('bluebird')

require('client/lib/jquery/boolean-attributes')
require('client/lib/jquery/register-plugin')
require('client/lib/comment-autocomplete')

const INPUT_SELECTOR = '.mix-select__input'
const CREATE_SELECTOR = '.mix-select__create'
const LIST_SELECTOR = '.mix-select__list'
const LIST_ITEM_SELECTOR = '.mix-list-item'
const LIST_ITEM_SUBTITLE_SELECTOR = '.mix-list-item__subtitle'
const COMMENT_TEXTAREA_SELECTOR = '.mix-select__comment-textarea'
const COMMENT_SUBMIT_SELECTOR = '.mix-select__comment-submit'

const CREATE_HIDDEN_CLASSNAME = 'mix-select__create--hidden'
const LIST_ITEM_HIDDEN_CLASSNAME = 'mix-list-item--hidden'

class MixSelectForm {
  constructor(el, options) {
    this.$el = $(el)
    this.options = _.defaults(options, {
      changeCallback: _.noop,
      submitCallback: _.noop
    })

    _.bindAll(this, [
      'onSubmit',
      'onInputChange',
      'onInputKeyDown',
      'onCreateButtonClick',
      'onListItemClick',
      'onCommentChange',
      'onCommentSubmit'
    ])

    this.cacheSelectors()
    this.cacheAttributes()
    this.registerEvents()
    this.setupCommentAutocomplete()
    this.onInputChange()
    this.onCommentChange()
  }

  destroy() {
    this.unregisterEvents()
    this.triggerUrlMixesUpdated()
  }

  cacheSelectors() {
    this.$input = this.$el.find(INPUT_SELECTOR)
    this.$createButton = this.$el.find(CREATE_SELECTOR)
    this.$list = this.$el.find(LIST_SELECTOR)
    this.$listItems = this.$list.find(LIST_ITEM_SELECTOR)
    this.$commentTextarea = this.$el.find(COMMENT_TEXTAREA_SELECTOR)
    this.$commentSubmit = this.$el.find(COMMENT_SUBMIT_SELECTOR)
  }

  cacheAttributes() {
    this.article = JSON.parse(this.$el.attr('data-body'))
    this.urlId = this.article['url_id']
    this.pageContext = {
      recId: this.$el.attr('data-rec-id'),
      recBatchId: this.$el.attr('data-rec-batch-id')
    }
  }

  registerEvents() {
    this.$el.on('submit', this.onSubmit)
    this.$input.on('input', this.onInputChange)
    this.$input.on('keydown', this.onInputKeyDown)
    this.$createButton.on('click', this.onCreateButtonClick)
    this.$list.on('click', LIST_ITEM_SELECTOR, this.onListItemClick)
    this.$commentTextarea.on('input', this.onCommentChange)
    this.$commentSubmit.on('click', this.onCommentSubmit)
  }

  unregisterEvents() {
    this.$el.off('submit', this.onSubmit)
    this.$input.off('input', this.onInputChange)
    this.$input.off('keydown', this.onInputKeyDown)
    this.$createButton.off('click', this.onCreateButtonClick)
    this.$list.off('click', LIST_ITEM_SELECTOR, this.onListItemClick)
    this.$commentTextarea.off('input', this.onCommentChange)
    this.$commentSubmit.off('click', this.onCommentSubmit)
  }

  setupCommentAutocomplete() {
    return UserModel.API.contacts()
    .then(response => {
      this.$commentTextarea.CommentAutocomplete(response)
    })
  }

  triggerUrlMixesUpdated() {
    // Send out a notification that the article's mixes have been updated
    const mixIds = _.map(this.$listItems.filter('[data-mixed]'), 'dataset.mixId')
    const data = { urlId: this.urlId, mixIds }
    if (typeof app === 'object' && typeof app.trigger === 'function') {
      app.trigger('update-url-mixes', data)
    }
    this.options.changeCallback(data)
  }

  $listItemsById(mixId) {
    return this.$listItems.filter(`[data-mix-id="${mixId}"]`)
  }

  $listItemsByName(mixName) {
    const mixSlug = nameUtils.prettyUrl(mixName)
    return this.$listItems.filter(`[data-mix-slug="${mixSlug}"]`)
  }

  addComment(commentText) {
    if (!commentText.length) {
      return alert({
        heading: 'Comment text required.',
        subheading: 'You must enter some text to post a comment.'
      })
    }
    return ArticleModel.API.addComment(this.urlId, commentText)
    .then(() => {
      toast({
        message: 'Woot! Your comment was added!'
      })
    })
    .catch(err => {
      let message = 'Something went wrong on our end. Try that again.'
      if (err.message === '`text` is required for comment submission') {
        message = 'Type something into the comment box and try again.'
      }
      alert({
        heading: 'Could not post your comment',
        subheading: message
      })
    })
  }

  createMix(mixName, isRecommended = false) {
    const cleanMixName = nameUtils.cleanName(mixName)
    if (!nameUtils.isValidMixName(cleanMixName)) {
      return Promise.reject(new Error('Invalid mix name: ' + mixName))
    }
    return MixModel.API.createMix(cleanMixName, isRecommended, this.pageContext)
    .then(createResponse => {
      return MixModel.API.addToMix(createResponse.id, this.urlId, this.pageContext)
      .then(addResponse => {
        const mix = _.merge({ isMixed: true }, createResponse, addResponse.mix)
        const $suggestedItem = this.$listItemsByName(mixName)
        // Render the created mix list item
        const listItemHtml = mixListItemsTemplate(_.merge({
          mixes: [mix],
          isRecommended
        }, viewLocals))
        if ($suggestedItem.length === 1) {
          // Replace the dummy "suggested" mix list item
          $suggestedItem.before(listItemHtml).remove()
        } else {
          // Add the new item to the top of the list
          this.$list.prepend(listItemHtml)
        }
        // Update the cached $listItems selector with the new item
        this.$listItems = this.$list.find(LIST_ITEM_SELECTOR)
        this.triggerUrlMixesUpdated()
        return addResponse
      })
    })
  }

  addToMix(mixId) {
    return MixModel.API.addToMix(mixId, this.urlId, this.pageContext)
    .then(() => {
      this.$listItemsById(mixId).boolData('mixed', true)
      this.triggerUrlMixesUpdated()
    })
  }

  removeFromMix(mixId) {
    return MixModel.API.removeFromMix(mixId, this.urlId, this.pageContext)
    .then(removeResponse => {
      const $listItem = this.$listItemsById(mixId)
      $listItem.boolData('mixed', false)
      if (removeResponse.mixDeleted) {
        // Work-around auto-deletion of empty suggested mixes
        $listItem.removeAttr('data-mix-id').boolData('recommended', true)
        $listItem.find(LIST_ITEM_SUBTITLE_SELECTOR).text('Suggested')
      }
      this.triggerUrlMixesUpdated()
    })
  }

  onSubmit(event) {
    event.preventDefault()
    this.options.submitCallback()
  }

  onInputChange() {
    const inputName = nameUtils.cleanName(this.$input.val())
    const isInputValid = nameUtils.isValidMixName(inputName)
    const isInputExactMatch = this.$listItemsByName(inputName).length === 1
    // Update the "Create" button
    this.$createButton
    .prop('disabled', !isInputValid)
    .toggleClass(CREATE_HIDDEN_CLASSNAME, !isInputValid || isInputExactMatch)
    // Un-hide all items if there is no filter
    if (!inputName) {
      this.$listItems.removeClass(LIST_ITEM_HIDDEN_CLASSNAME)
      return
    }
    // Hide items whose slug doesn't match the input
    const inputSlug = nameUtils.prettyUrl(inputName)
    const inputSlugMatcher = slugMatcher(inputSlug)
    this.$listItems.each((index, itemElem) => {
      const isHidden = !inputSlugMatcher(itemElem.dataset.mixSlug)
      $(itemElem).toggleClass(LIST_ITEM_HIDDEN_CLASSNAME, isHidden)
    })
  }

  onInputKeyDown(event) {
    if (event.keyCode === 13) { // Enter
      this.onCreateButtonClick(event)
    }
  }

  onCreateButtonClick(event) {
    event.preventDefault()
    const inputName = nameUtils.cleanName(this.$input.val())
    if (!inputName || this.$createButton.prop('disabled')) {
      return
    }
    // Toggle the existing mix list item if it exists
    const $existingItem = this.$listItemsByName(inputName)
    if ($existingItem.length === 1) {
      $existingItem.click()
    } else {
      // Disable the field during creation
      this.$input.prop('disabled', true)
      this.$createButton.prop('disabled', true)
      this.createMix(inputName)
      .then(() => {
        // Reset the field after creation
        this.$input.val('').trigger('input')
        this.$input.prop('disabled', false)
      })
      .catch(() => {
        // Unlock the field, even if creation fails
        this.$input.prop('disabled', false)
      })
    }
  }

  onListItemClick(event) {
    event.preventDefault()
    const $listItem = $(event.currentTarget)
    if ($listItem.boolData('processing')) {
      return Promise.resolve() // Prevent duplicate actions
    }
    $listItem.boolData('processing', true)
    const mixId = $listItem.attr('data-mix-id')
    const isMixed = $listItem.boolData('mixed')
    const mixName = $listItem.attr('data-mix-name')
    const isRecommended = $listItem.boolData('recommended')
    return (isMixed
      ? this.removeFromMix(mixId)
      : (mixId
        ? this.addToMix(mixId)
        : this.createMix(mixName, isRecommended)))
    .catch(err => {
      alert({
        heading: 'Action failed',
        subheading: 'Something went wrong on our end. Try that again.'
      })
      throw err
    })
    .finally(() => {
      $listItem.boolData('processing', false)
    })
  }

  onCommentChange() {
    const isDisabled = _.isEmpty(this.$commentTextarea.val().trim())
    this.$commentSubmit.prop('disabled', isDisabled)
  }

  onCommentSubmit(event) {
    event.preventDefault()
    const commentText = this.$commentTextarea.val().trim()
    if (commentText) {
      return this.addComment(commentText)
      .then(() => {
        this.$commentTextarea.val('').trigger('input')
      })
    }
  }
}

$.registerPlugin(MixSelectForm, 'MixSelectForm')

module.exports = MixSelectForm

function slugMatcher(inputSlug) {
  const startsWith = inputSlug.length < 2 ? '^' : ''
  const inputRegexp = new RegExp(startsWith + _.escapeRegExp(inputSlug), 'i')
  return function (itemElem) {
    return inputRegexp.test(itemElem)
  }
}
